import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Card, money, Th, Td, DateRangeFilter, rangeParams } from "../components/shared";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { DollarSign, TrendingUp, Target, Wallet, PieChart, CalendarDays } from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className={`h-7 w-7 rounded-md flex items-center justify-center ${color}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    </div>
    <div className="font-mono text-xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [rangeSel, setRangeSel] = useState({ range: "today" });

  useEffect(() => {
    const fetchData = () => api.get(`/dashboard?${rangeParams(rangeSel)}`).then((r) => setData(r.data));
    fetchData();
    const timer = setInterval(fetchData, 60000);
    return () => clearInterval(timer);
  }, [rangeSel]);

  if (!data) return <div className="text-slate-400 text-sm animate-pulse">Loading dashboard…</div>;
  const k = data.kpis;
  const p = data.period;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Full-funnel performance across email, websites and networks"
        action={<DateRangeFilter value={rangeSel} onChange={setRangeSel} />}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-4 fade-up">
        <KpiCard icon={DollarSign} label="Today Revenue" value={money(p.today_revenue)} color="bg-emerald-50 text-emerald-600" />
        <KpiCard icon={Target} label="Today Conversions" value={p.today_conversions.toLocaleString()} color="bg-indigo-50 text-indigo-600" />
        <KpiCard icon={DollarSign} label="Yesterday Revenue" value={money(p.yesterday_revenue)} color="bg-blue-50 text-blue-600" />
        <KpiCard icon={Wallet} label="This Week Revenue" value={money(p.week_revenue)} color="bg-amber-50 text-amber-600" />
        <KpiCard icon={TrendingUp} label="This Month Revenue" value={money(p.month_revenue)} color="bg-emerald-50 text-emerald-600" />
      </div>

      <Card className="px-4 py-3 mb-6 flex flex-wrap gap-x-8 gap-y-2 items-center fade-up" data-testid="range-stats-strip">
        <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Selected range</span>
        {[["Clicks", k.clicks.toLocaleString()], ["Conversions", k.conversions.toLocaleString()], ["Revenue", money(k.revenue)], ["Cost", money(k.cost)], ["Profit", money(k.profit)], ["CR", `${k.cr}%`], ["EPC", `$${k.epc}`], ["ROI", `${k.roi}%`]].map(([l, v]) => (
          <div key={l} className="flex items-baseline gap-1.5">
            <span className="text-[11px] text-slate-400">{l}</span>
            <span className="font-mono text-sm font-semibold text-slate-900">{v}</span>
          </div>
        ))}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 xl:col-span-2">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-indigo-500" /> Performance Timeline
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="l" dataKey="clicks" fill="#c7d2fe" radius={[3, 3, 0, 0]} name="Clicks" />
              <Line yAxisId="r" dataKey="revenue" stroke="#059669" strokeWidth={2} dot={false} name="Revenue $" />
              <Line yAxisId="r" dataKey="conversions" stroke="#4f46e5" strokeWidth={2} dot={false} name="Conversions" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-emerald-500" /> This Month — Daily Revenue
          </h3>
          <div className="max-h-[260px] overflow-y-auto scrollbar-thin" data-testid="month-daily-list">
            {data.month_daily.map((d) => (
              <div key={d.date} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-xs font-medium text-slate-700">
                  {new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                </span>
                <div>
                  <span className="text-[10px] text-slate-400 mr-2">{d.conversions} conv</span>
                  <span className="font-mono text-xs font-semibold text-emerald-600">{money(d.revenue)}</span>
                </div>
              </div>
            ))}
            {data.month_daily.length === 0 && <div className="text-xs text-slate-400">No revenue yet this month.</div>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-0 xl:col-span-2 overflow-hidden">
          <h3 className="font-heading text-sm font-semibold text-slate-900 px-5 pt-5 pb-3">Top Campaigns</h3>
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><Th>Campaign</Th><Th className="text-right">Clicks</Th><Th className="text-right">Conv.</Th><Th className="text-right">CR</Th><Th className="text-right">EPC</Th><Th className="text-right">Revenue</Th></tr></thead>
            <tbody>
              {data.top_campaigns.map((c) => (
                <tr key={c.name} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-900">{c.name}</Td>
                  <Td className="text-right font-mono">{c.clicks.toLocaleString()}</Td>
                  <Td className="text-right font-mono">{c.conversions}</Td>
                  <Td className="text-right font-mono">{c.cr}%</Td>
                  <Td className="text-right font-mono">${c.epc}</Td>
                  <Td className="text-right font-mono font-semibold text-emerald-600">{money(c.revenue)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <PieChart className="h-4 w-4 text-indigo-500" /> Traffic Origin Funnel
            </h3>
            {data.sources.map((s) => (
              <div key={s.name} className="mb-3 last:mb-0">
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-slate-700 truncate">{s.name}</span>
                  <span className="font-mono text-slate-500">{s.clicks.toLocaleString()} → {s.conversions} ({s.cr}%)</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, (s.clicks / Math.max(...data.sources.map((x) => x.clicks))) * 100)}%` }} />
                </div>
              </div>
            ))}
          </Card>
          <Card className="p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Networks Breakdown</h3>
            {data.networks.map((n) => (
              <div key={n.name} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
                <span className="text-xs font-medium text-slate-700">{n.name}</span>
                <div className="text-right">
                  <span className="font-mono text-xs font-semibold text-emerald-600">{money(n.revenue)}</span>
                  <span className="text-[10px] text-slate-400 ml-2">{n.conversions} conv</span>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
